import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Truck, MapPin, Calendar, User, Shirt, Printer, Loader2, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

/**
 * Modal de comprobante de recolección de ropa sucia.
 * Muestra los datos de la recolección y permite reimprimir el ticket
 * (dos copias con espacios para firmar), igual que en la entrega.
 */
const DetalleRecoleccionModal = ({ isOpen, onClose, recoleccion }) => {
  const [isPrinting, setIsPrinting] = useState(false);

  if (!recoleccion) return null;

  const getTotalItems = () => {
    return recoleccion.items?.reduce((sum, i) => sum + (i.cantidad || 0), 0) || 0;
  };

  const getPesoKg = () => {
    const gramos = recoleccion.peso_real != null ? recoleccion.peso_real : recoleccion.peso_total;
    return ((gramos || 0) / 1000).toFixed(2);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleReimprimir = async () => {
    setIsPrinting(true);
    try {
      const printer = getPrinterInstance();
      await printer.printRecoleccionDobleCopia({
        consecutivo: recoleccion.consecutivo_recoleccion || recoleccion.solicitud_id,
        fecha: recoleccion.fecha_entrega || recoleccion.createdAt,
        edificio: recoleccion.edificio,
        hospedaje: recoleccion.hospedaje,
        receptor_nombre: recoleccion.receptor_nombre,
        conductor: recoleccion.conductor || recoleccion.recolector,
        items: recoleccion.items,
        peso_real: recoleccion.peso_real,
        peso_total: recoleccion.peso_total,
      });
      toast.success('Comprobante reimpreso correctamente (2 copias)');
    } catch (error) {
      console.error('Error al reimprimir:', error);
      toast.error(error.message || 'Error al reimprimir el comprobante');
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Truck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Comprobante de Recolección
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Ropa sucia · Lencería Vertical
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Información de la recolección */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <MapPin className="h-5 w-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Edificio</p>
                        <p className="font-semibold text-sm">{recoleccion.edificio?.nombre}</p>
                        <p className="text-xs text-gray-600">{recoleccion.edificio?.ciudad}</p>
                      </div>
                    </div>

                    {(recoleccion.hospedaje?.nombre || recoleccion.hospedaje?.nombrePropiedad) && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <Home className="h-5 w-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Activo</p>
                          <p className="font-semibold text-sm">
                            {recoleccion.hospedaje?.nombre || recoleccion.hospedaje?.nombrePropiedad}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Fecha de Recolección</p>
                        <p className="font-semibold text-sm">
                          {formatFecha(recoleccion.fecha_entrega || recoleccion.createdAt)}
                        </p>
                      </div>
                    </div>

                    {(recoleccion.conductor || recoleccion.recolector) && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <User className="h-5 w-5 text-purple-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Recolectado por</p>
                          <p className="font-semibold text-sm">
                            {(recoleccion.conductor || recoleccion.recolector)?.nombre || 'N/A'}
                          </p>
                        </div>
                      </div>
                    )}

                    {recoleccion.receptor_nombre && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <User className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Entregado por</p>
                          <p className="font-semibold text-sm">{recoleccion.receptor_nombre}</p>
                        </div>
                      </div>
                    )}

                    {(recoleccion.consecutivo_recoleccion || recoleccion.solicitud_id) && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <Truck className="h-5 w-5 text-orange-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Consecutivo</p>
                          <p className="font-semibold text-sm font-mono">
                            {recoleccion.consecutivo_recoleccion || recoleccion.solicitud_id}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Observaciones */}
                  {recoleccion.observaciones && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Observaciones</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {recoleccion.observaciones}
                      </p>
                    </div>
                  )}
                </div>

                {/* Items recolectados */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Shirt className="h-5 w-5 text-orange-600" />
                      Prendas Recolectadas
                    </h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        {getTotalItems()} items
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {getPesoKg()} kg
                      </Badge>
                    </div>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                            Item
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400">
                            Peso (g)
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                            Cantidad
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {recoleccion.items?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                            <td className="px-4 py-3 text-sm">{item.nombre}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-600">
                              {item.peso || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">{item.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Firma */}
                {recoleccion.firma && (
                  <div className="mb-2">
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <User className="h-5 w-5 text-green-600" />
                      Firma de quien entrega
                    </h3>
                    <div className="border-2 border-green-200 dark:border-green-800 rounded-lg p-4 bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
                      <img
                        src={recoleccion.firma}
                        alt="Firma"
                        className="max-w-full h-40 mx-auto object-contain bg-white dark:bg-gray-800 rounded p-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {recoleccion.receptor_nombre && (
                        <p className="text-center text-xs text-gray-600 dark:text-gray-400 mt-2">
                          Firmado por: {recoleccion.receptor_nombre}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 p-6 border-t bg-gray-50 dark:bg-gray-900">
                <Button
                  variant="outline"
                  onClick={handleReimprimir}
                  disabled={isPrinting}
                >
                  {isPrinting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Printer className="h-4 w-4 mr-2" />
                  )}
                  {isPrinting ? 'Imprimiendo...' : 'Reimprimir comprobante'}
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DetalleRecoleccionModal;
